// ANA-R3-GEOM-05 — session-only on-tile geometry override store.
//
// Findings are immutable in the DB (`ai_defect_findings.finding_path`). The
// analyst can nonetheless reshape a finding's geometry ON ITS TILE; that edit
// is held HERE — in browser session only — keyed `findingId → overridePath`
// (NORMALISED 0..1, flat `[x0,y0,…]` with the GeoJSON closing duplicate for
// closed shapes, i.e. the `serialisePoints` form). When an override exists the
// tile renders the MODIFIED geometry + crop framing and hides the original; a
// per-tile reset discards it; an "edited" badge marks overridden tiles.
//
// This is PURE UI STATE (story §2): a Zustand store, NOT persisted (no
// localStorage), NOT in the TanStack cache, NEVER sent at edit time. It is
// cleared on reload (the store is re-created on every page load) and on a
// data-acquisition switch (`resetForDataAcquisition` — AC5). On a successful
// Apply/promote the host clears that finding's override (`clear`).
//
// The store also records the editor MODE per finding alongside the override
// path, so a rect override re-seeds as a constrained-resize rect and a polygon
// re-seeds as a free-form polygon when the editor remounts (AC4). The mode is
// derivable from the path via `detectMode`, but storing it avoids a rect that
// the analyst dragged into a non-axis-aligned quad silently snapping back.
import { create } from "zustand";
import type { EditorMode } from "@aeriosiq/ui";

/** A single session override: the NORMALISED flat path + the editor mode. */
export interface TileGeometryOverride {
  /** Flat `[x0,y0,…]` NORMALISED 0..1, serialised form (closing dup for closed). */
  path: number[];
  /** Editor mode the override was committed in (rect / polygon / polyline). */
  mode: EditorMode;
}

export interface TileGeometryOverrideState {
  /** Active data-acquisition id — bumping it clears all overrides (AC5). */
  dataAcquisitionId: string | null;
  /** findingId → override. Absent ⇒ the tile shows the original geometry. */
  overrides: Record<string, TileGeometryOverride>;

  /**
   * Set (or replace) the override for `findingId`. `path` is the NORMALISED
   * serialised flat array; `mode` the editor mode. Called when the analyst
   * commits an on-tile edit (the TileGeometryEditor's Save).
   */
  set: (findingId: string, override: TileGeometryOverride) => void;
  /**
   * Discard `findingId`'s override (per-tile reset-to-original AND the
   * post-Apply clear). A no-op when none exists.
   */
  clear: (findingId: string) => void;
  /** True iff `findingId` currently has a session override. */
  has: (findingId: string) => boolean;
  /** The override for `findingId`, or null. */
  get: (findingId: string) => TileGeometryOverride | null;
  /**
   * Switch the active data-acquisition. When `dataAcquisitionId` changes this
   * drops every override (overrides never leak across DAs — AC5). Idempotent
   * for the same id so re-renders don't wipe in-progress edits.
   */
  resetForDataAcquisition: (dataAcquisitionId: string) => void;
  /** Drop all overrides (test seam + explicit clear). */
  resetAll: () => void;
}

export const useTileGeometryOverrideStore = create<TileGeometryOverrideState>(
  (set, getState) => ({
    dataAcquisitionId: null,
    overrides: {},

    set: (findingId, override) => {
      set((s) => ({
        overrides: { ...s.overrides, [findingId]: override },
      }));
    },

    clear: (findingId) => {
      set((s) => {
        if (!(findingId in s.overrides)) return s;
        // Omit the key without a dynamic `delete` (lint rule).
        const next: Record<string, TileGeometryOverride> = {};
        for (const [id, ov] of Object.entries(s.overrides)) {
          if (id !== findingId) next[id] = ov;
        }
        return { overrides: next };
      });
    },

    has: (findingId) => findingId in getState().overrides,

    get: (findingId) => getState().overrides[findingId] ?? null,

    resetForDataAcquisition: (dataAcquisitionId) => {
      set((s) => {
        if (s.dataAcquisitionId === dataAcquisitionId) return s;
        // A different DA (or first mount) ⇒ wipe all overrides (AC5).
        return { dataAcquisitionId, overrides: {} };
      });
    },

    resetAll: () => {
      set({ overrides: {} });
    },
  }),
);

// Test seam — lets a Playwright `page.evaluate` read/seed overrides without
// driving the tile editor by hand (mirrors `selectionStore`'s window hook).
interface WindowWithOverrideStore {
  __wpTileGeometryOverrideStore?: typeof useTileGeometryOverrideStore;
}
if (typeof window !== "undefined") {
  (window as unknown as WindowWithOverrideStore).__wpTileGeometryOverrideStore =
    useTileGeometryOverrideStore;
}
